import { useState } from 'react';

import { getCookie } from '@api/HelperAPI';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getHasOnlyRCFOffer } from '@helpers/FormalOffers/getHasOnlyRCFOffer';
import { getIsRCFOffer } from '@helpers/FormalOffers/getIsRCFOffer';
import { getTimeOutDate } from '@helpers/FormalOffers/getTimeOutDate';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { Card, Text } from '@iwoca/orion';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { CashlessOfferDetails } from './CashlessOfferDetails';
import { CashlessProductExplanation } from './CashlessProductExplanation';
import { ChooseAmount } from './ChooseAmount';
import { OfferDetails } from './OfferDetails';
import { RCFProductExplanation } from './RCFProductExplanation';
import { ShowFlexibleOffersCard } from './ShowFlexibleOffersCard';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';

export const RCF_CASHLESS_OPENING_COOKIE = 'rcfCashlessOpening';

export const RCFApprovedView = ({
    formalOffers,
    showOtherOffers,
    initialAmount
}: {
    formalOffers: TFormalOffer[];
    showOtherOffers: () => void;
    initialAmount?: number;
}) => {
    const isCashlessOpening =
        getCookie(RCF_CASHLESS_OPENING_COOKIE) !== 'false';

    if (isCashlessOpening) {
        const rcfOffer = formalOffers.find((offer) => getIsRCFOffer(offer));

        if (!rcfOffer) {
            logError('RCF offer not found', { tags: TAGS.DE_LENDING });
            return <FallbackErrorModal />;
        }

        const hasOnlyRCFOffer = getHasOnlyRCFOffer(formalOffers);

        return (
            <div className="mx-auto max-w-[960px] space-y-xl px-s">
                <div className="space-y-l text-center">
                    <h1 className="my-0">
                        <FormattedMessage
                            id="_.rcf_approved_view.title"
                            defaultMessage="Your Flexi credit line with {offerAmount}"
                            values={{
                                offerAmount: (
                                    <span className="text-success-drk">
                                        <FormattedCurrency
                                            value={rcfOffer.available_cash!}
                                        />
                                    </span>
                                )
                            }}
                        />
                    </h1>
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.timeout_window"
                            defaultMessage="This offer is valid until {timeoutDate}"
                            values={{
                                timeoutDate: (
                                    <FormattedDate
                                        value={getTimeOutDate([rcfOffer])}
                                        day="numeric"
                                        month="short"
                                        year="numeric"
                                    />
                                )
                            }}
                        />
                    </Text>
                </div>
                <div className="flex flex-col gap-l min-m:flex-row min-m:items-start">
                    <div className="flex-1">
                        <CashlessProductExplanation />
                    </div>
                    <div className="flex-1 space-y-l">
                        <CashlessOfferDetails offer={rcfOffer} />
                        {!hasOnlyRCFOffer && (
                            <ShowFlexibleOffersCard
                                showOtherOffers={showOtherOffers}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <RCFApprovedViewWithDrawdown
            formalOffers={formalOffers}
            showOtherOffers={showOtherOffers}
            initialAmount={initialAmount}
        />
    );
};

const RCFApprovedViewWithDrawdown = ({
    formalOffers,
    showOtherOffers,
    initialAmount
}: {
    formalOffers: TFormalOffer[];
    showOtherOffers: () => void;
    initialAmount?: number;
}) => {
    const [selectedAmount, setSelectedAmount] = useState<number | null>(
        initialAmount || null
    );
    const hasOnlyRCFOffer = getHasOnlyRCFOffer(formalOffers);
    const rcfOffer = formalOffers.find((offer) => getIsRCFOffer(offer));

    if (!rcfOffer) {
        logError('RCF offer not found', {
            tags: TAGS.DE_LENDING
        });

        return <FallbackErrorModal />;
    }

    const offerAmount = rcfOffer.available_cash!;
    const offerTimeoutDate = getTimeOutDate([rcfOffer]);

    return (
        <div className="mx-auto max-w-[960px] space-y-xl px-s">
            <div className="space-y-l text-center">
                <h1 className="my-0" data-testid="rcf-offer-title">
                    <FormattedMessage
                        id="_.rcf_approved_view.title"
                        defaultMessage="Your Flexi credit line with {offerAmount}"
                        values={{
                            offerAmount: (
                                <span className="text-success-drk">
                                    <FormattedCurrency value={offerAmount} />
                                </span>
                            )
                        }}
                    />
                </h1>
                <Text>
                    <FormattedMessage
                        id="_.rcf_approved_view.timeout_window"
                        defaultMessage="This offer is valid until {timeoutDate}"
                        values={{
                            timeoutDate: (
                                <FormattedDate
                                    value={offerTimeoutDate}
                                    day="numeric"
                                    month="short"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                </Text>
            </div>

            <div className="flex flex-col items-start gap-l min-m:flex-row">
                <Card className="flex-1 space-y-xl p-xl">
                    <ChooseAmount
                        selectedAmount={selectedAmount}
                        setSelectedAmount={setSelectedAmount}
                        maxAmount={offerAmount}
                    />
                    <RCFProductExplanation />
                </Card>
                <div className="flex w-full flex-1 flex-col gap-l">
                    <OfferDetails
                        offer={rcfOffer}
                        selectedAmount={selectedAmount}
                    />
                    {!hasOnlyRCFOffer ? (
                        <ShowFlexibleOffersCard
                            showOtherOffers={showOtherOffers}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};
